import React, { useState, useEffect } from "react";
import {
  Link,
  useLocation,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  ArrowLeft,
  Mail,
  Reply,
  CheckCircle,
  XCircle,
  Clock,
  User,
  Calendar,
  ChevronDown,
  ChevronUp,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  RotateCcw,
  ChevronRight,
  Brain,
  MailOpen,
} from "lucide-react";
import moment from "moment";
import { Drawer, Modal, Tooltip } from "flowbite-react";
import { clog, getPlanDetails, isUserOwnerOfWorkspace } from "../lib/utils";
import axios from "axios";
import UpgradeModal from "../components/UpgradeModal";
import FailureModal from "../components/FailureModal";
import DetailsDrawer from "../components/DetailsDrawer";

const EmailCard = ({
  email,
  isReply = false,
  isExpanded = false,
  onToggle,
}) => {
  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4 mb-3">
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3 flex-1">
          <div
            className={`w-8 h-8 rounded-full flex items-center justify-center ${
              isReply ? "bg-[#EEF2FF]" : "bg-[#F0F9FF]"
            }`}
          >
            {isReply ? (
              <Reply className="w-4 h-4 text-[#6366F1]" />
            ) : (
              <Mail className="w-4 h-4 text-[#0EA5E9]" />
            )}
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-[600] text-[#1F2937] text-[14px]">
                {email.subject}
              </span>
              {email.status && (
                <span
                  className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                    email.status === "sent"
                      ? "bg-[#DBEAFE] text-[#1E40AF]"
                      : email.status === "replied"
                      ? "bg-[#D1FAE5] text-[#065F46]"
                      : email.status === "opened"
                      ? "bg-[#FEF3C7] text-[#92400E]"
                      : "bg-[#F3F4F6] text-[#374151]"
                  }`}
                >
                  {email.status}
                </span>
              )}
            </div>
            <div className="text-[#6B7280] text-[12px] mb-2">
              {isReply
                ? `Reply from ${email.sender}`
                : `To: ${email.recipient || "Multiple recipients"}`}{" "}
              • {email.timestamp}
            </div>
            <div className="text-[#374151] text-[13px] leading-relaxed">
              {isExpanded
                ? email.content
                : `${email.content.substring(0, 100)}...`}
            </div>
          </div>
        </div>
        <button
          onClick={onToggle}
          className="text-[#6B7280] hover:text-[#374151] ml-2"
        >
          {isExpanded ? (
            <ChevronUp className="w-4 h-4" />
          ) : (
            <ChevronDown className="w-4 h-4" />
          )}
        </button>
      </div>
    </div>
  );
};

const InboxSequence = ({ campaign, expandedEmails, setExpandedEmails }) => {
  const toggleEmail = (emailId) => {
    setExpandedEmails((prev) => ({
      ...prev,
      [emailId]: !prev[emailId],
    }));
  };

  return (
    <div className="bg-[#FAFBFC] rounded-[20px] border border-[#E5E7EB] p-6 mb-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-[600] text-[#1F2937] text-[18px] mb-1">
            {campaign.name}
          </h3>
          <div className="flex items-center gap-4 text-[#6B7280] text-[14px]">
            <span className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              Started: {campaign.startDate}
            </span>
            <span className="flex items-center gap-1">
              <User className="w-4 h-4" />
              {campaign.totalLeads} leads
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              campaign.status === "active"
                ? "bg-[#D1FAE5] text-[#065F46]"
                : campaign.status === "paused"
                ? "bg-[#FEF3C7] text-[#92400E]"
                : "bg-[#F3F4F6] text-[#374151]"
            }`}
          >
            {campaign.status}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {campaign.emails.map((email, index) => (
          <div key={email.id}>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 bg-[#5E78F4] text-white rounded-full flex items-center justify-center text-xs font-medium">
                {index + 1}
              </div>
              <span className="font-[500] text-[#374151] text-[14px]">
                Email {index + 1}
              </span>
              <span className="text-[#6B7280] text-[12px]">
                ({email.sentCount} sent, {email.repliesCount} replies)
              </span>
            </div>

            <EmailCard
              email={email}
              isExpanded={expandedEmails[email.id]}
              onToggle={() => toggleEmail(email.id)}
            />

            {/* Show replies if any */}
            {email.replies && email.replies.length > 0 && (
              <div className="ml-8 space-y-2">
                {email.replies.map((reply) => (
                  <EmailCard
                    key={reply.id}
                    email={reply}
                    isReply={true}
                    isExpanded={expandedEmails[reply.id]}
                    onToggle={() => toggleEmail(reply.id)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const LeadCard = ({ lead }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case "replied":
        return "bg-[#D1FAE5] text-[#065F46]";
      case "opened":
        return "bg-[#FEF3C7] text-[#92400E]";
      case "sent":
        return "bg-[#DBEAFE] text-[#1E40AF]";
      case "bounced":
        return "bg-[#FEE2E2] text-[#991B1B]";
      default:
        return "bg-[#F3F4F6] text-[#374151]";
    }
  };

  return (
    <div className="bg-white rounded-[12px] border border-[#E5E7EB] p-4">
      <div className="flex items-start justify-between mb-3">
        <div>
          <h4 className="font-[600] text-[#1F2937] text-[14px] mb-1">
            {lead.name}
          </h4>
          <p className="text-[#6B7280] text-[12px] mb-2">{lead.email}</p>
          <p className="text-[#6B7280] text-[12px]">
            {lead.company} • {lead.position}
          </p>
        </div>
        <span
          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
            lead.status
          )}`}
        >
          {lead.status}
        </span>
      </div>

      <div className="flex items-center justify-between text-[#6B7280] text-[11px]">
        <span>Last contacted: {lead.lastContacted}</span>
        <span>{lead.emailsSent} emails sent</span>
      </div>

      {lead.lastReply && (
        <div className="mt-3 p-2 bg-[#F9FAFB] rounded-[8px] border border-[#E5E7EB]">
          <p className="text-[#374151] text-[12px] leading-relaxed">
            "{lead.lastReply}"
          </p>
        </div>
      )}
    </div>
  );
};

export const EmailCampaigns = () => {
  // providerId variable from searchParams
  const { providerId } = useParams();

  let decryptProviderId = atob(providerId);

  clog("decrypt", decryptProviderId);

  // Check if user is owner of workspace
  const isOwner = isUserOwnerOfWorkspace();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [provider, setProvider] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("");
  const [leads, setLeads] = useState([]);
  const [leadsEmails, setLeadsEmails] = useState([]);
  const [activeTab, setActiveTab] = useState("inbox");
  const [expandedEmails, setExpandedEmails] = useState({});
  const [timelineZoom, setTimelineZoom] = useState(1); // 1 = normal, 1.5 = zoomed in, 0.75 = zoomed out
  const [collapsedColumns, setCollapsedColumns] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState("active");
  // const [isTimelineExpanded, setIsTimelineExpanded] = useState(false);

  // Row collapse state
  const [collapsedRows, setCollapsedRows] = useState({});
  const [allRowsCollapsed, setAllRowsCollapsed] = useState(false);

  // Drawer state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [drawerData, setDrawerData] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);

  // Upgrade modal state
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Failure modal state
  const [openFailureModal, setopenFailureModal] = useState(undefined);
  const [successFailureMessage, setsuccessFailureMessage] = useState("");

  // show thinking modal state
  const [showThinkingModal, setShowThinkingModal] = useState(false);
  const [thinkingModalData, setThinkingModalData] = useState(null);

  const toggleRowCollapse = (leadId) => {
    setCollapsedRows((prev) => ({
      ...prev,
      [leadId]: !prev[leadId],
    }));
    setAllRowsCollapsed(false);
  };

  const removeModal = () => {
    setopenFailureModal(undefined);
  };

  const collapseAllRows = (leads) => {
    const collapsed = {};
    leads.forEach((l) => {
      collapsed[l.id] = true;
    });
    setCollapsedRows(collapsed);
    setAllRowsCollapsed(true);
  };

  const expandAllRows = () => {
    setCollapsedRows({});
    setAllRowsCollapsed(false);
  };

  // Get selected campaign object
  const getSelectedCampaignData = () => {
    return campaigns.find(
      (campaign) => campaign.id.toString() === selectedCampaign
    );
  };

  function getSentimentBadge(is_positive) {
    //is_positive 1 then positive is_positive 2 then nuetral and is_positive 0 negative
    switch (is_positive) {
      case "positive":
        return (
          <div className="inline-flex items-center gap-1 bg-[#ECFDF5] text-[#059669] text-[9px] px-2 py-1 rounded-[4px]">
            <span>Positive</span>
          </div>
        );
      case "neutral":
        return (
          <div className="inline-flex items-center gap-1 bg-[#DBEAFE] text-[#1E40AF] text-[9px] px-2 py-1 rounded-[4px]">
            <span>Neutral</span>
          </div>
        );
      case "negative":
        return (
          <div className="inline-flex items-center gap-1 bg-[#FEE2E2] text-[#991B1B] text-[9px] px-2 py-1 rounded-[4px]">
            <span>Negative</span>
          </div>
        );
      default:
        return null;
    }
  }

  function getRepliedBadgeAccordingToSentiment(is_positive) {
    //is_positive 1 then positive is_positive 2 then nuetral and is_positive 0 negative
    switch (is_positive) {
      case "positive":
        return (
          <div className="inline-block bg-[#ECFDF5] text-[#059669] text-[11px] px-2 py-1 rounded-[6px]">
            Replied
          </div>
        );
      case "neutral":
        return (
          <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
            Replied
          </div>
        );
      case "negative":
        return (
          <div className="inline-block bg-[#FEE2E2] text-[#991B1B] text-[11px] px-2 py-1 rounded-[6px]">
            Replied
          </div>
        );
      default:
        return (
          <div className="inline-block bg-[#f6f6f6] text-[#000000] text-[11px] px-2 py-1 rounded-[6px]">
            Replied
          </div>
        );
    }
  }

  useEffect(() => {
    fetchCampaignsData();
  }, [decryptProviderId]);

  const fetchLeadDetails = async (email) => {
    try {
      setDrawerLoading(true);
      setIsDrawerOpen(true);

      // Get workspace_id from localStorage
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      const response = await axios.get(
        `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/visonleads/leads/email_lead`,
        // `${process.env.REACT_APP_BASE_SIGNIN_URL}/api/visonleads/leads/email_lead?email=tom@affirmedsystems.com`,
        {
          params: {
            email: email,
            workspace_id: selectedWorkspace?.workspace_id || null,
          },
          headers: {
            "Content-Type": "application/json",
            jwt_token: localStorage.getItem("login_token"),
          },
        }
      );

      if (response.data && !response.data.error) {
        setDrawerData({
          ...response?.data?.data,
          lastSession: response?.data?.data?.summary?.lastSession || {},
          summary: {
            ...response?.data?.data?.summary,
            // duration: calculatedDuration,
            duration: response?.data?.data?.company?.summary?.duration,
          },
        });
      } else {
        console.error("Failed to fetch lead details:", response.data.error);
        setDrawerData(null);
        setIsDrawerOpen(false);
        setsuccessFailureMessage(
          response.data.message || "Failed to fetch lead details"
        );
        setopenFailureModal("dismissible");
      }
    } catch (error) {
      console.error("Error fetching lead details:", error);
      setDrawerData(null);
      setIsDrawerOpen(false);
      setsuccessFailureMessage(
        error.message || "An error occurred while fetching lead details"
      );
      setopenFailureModal("dismissible");
    } finally {
      setDrawerLoading(false);
    }
  };

  const fetchCampaignsData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get workspace_id from localStorage
      const selectedWorkspace = JSON.parse(
        localStorage.getItem("selected_workspace") || "{}"
      );

      // Fetch provider details
      const providerResponse = await fetch(
        `${
          process.env.REACT_APP_BASE_SIGNIN_URL
        }/email/api/email-providers/provider/${decryptProviderId}?workspace_id=${
          selectedWorkspace?.workspace_id || ""
        }`
      );
      const providerData = await providerResponse.json();
      if (!providerData.error) {
        setProvider(providerData.data);
      }

      // Fetch campaigns for this provider
      const campaignsResponse = await fetch(
        `${
          process.env.REACT_APP_BASE_SIGNIN_URL
        }/email/api/campaign-list?workspace_id=${
          selectedWorkspace?.workspace_id || ""
        }`
      );

      if (!campaignsResponse.ok) {
        throw new Error(
          `Failed to fetch campaigns: ${campaignsResponse.status}`
        );
      }

      const campaignsData = await campaignsResponse.json();

      // Handle direct array response or wrapped response
      const campaignsArray = Array.isArray(campaignsData)
        ? campaignsData
        : campaignsData.data || [];

      setCampaigns(campaignsArray);
      // Set the first campaign as selected by default if available
      if (campaignsArray.length > 0) {
        setSelectedCampaign(campaignsArray[0].id.toString());
      }

      // Fetch leads for this provider
      const leadsResponse = await fetch(
        `${
          process.env.REACT_APP_BASE_SIGNIN_URL
        }/email/api/leads/${decryptProviderId}?workspace_id=${
          selectedWorkspace?.workspace_id || ""
        }`
      );
      const leadsData = await leadsResponse.json();
      if (!leadsData.error) {
        setLeads(leadsData.data || []);
      }
      // Fetch leads for this provider
      const leadsEmailsResponse = await fetch(
        `${
          process.env.REACT_APP_BASE_SIGNIN_URL
        }/email/api/email-providers/leads/${decryptProviderId}?workspace_id=${
          selectedWorkspace?.workspace_id || ""
        }`
        // `${process.env.REACT_APP_BASE_SIGNIN_URL}/email/api/email-providers/leads/19`
      );
      const leadsEmailsData = await leadsEmailsResponse.json();
      if (!leadsEmailsData.error) {
        let emails = leadsEmailsData.data || [];
        emails = emails.map((emailMain) => ({
          ...emailMain,
          eamil: emailMain?.eamil?.sort((a, b) => a.id - b.id) || [],
        }));
        setLeadsEmails(emails);
        setAllRowsCollapsed(true);
        collapseAllRows(emails);
      }
    } catch (error) {
      console.error("Failed to fetch campaigns data:", error);
      setError(error.message || "Failed to load campaigns data");
    } finally {
      setLoading(false);
    }
  };

  // const shouldHideData = getPlanDetails() == 6;

  // Helper to format milliseconds into HH:MM:SS
  const formatDuration = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    return [h, m, s].map((n) => String(n).padStart(2, "0")).join(":");
  };

  // Icon component to use SVG symbols
  const Icon = ({ name, className }) => (
    <svg className={className}>
      <use href={`#icon_${name}`} />
    </svg>
  );

  // User Avatar Component
  const UserAvatar = ({ name, url }) => {
    const [imgError, setImgError] = useState(false);

    // If URL is provided and there's no load error, render the image
    if (url && !imgError) {
      return (
        <img
          src={url}
          alt={name}
          className="w-[110px] h-[110px] rounded-full object-cover"
          onError={() => setImgError(true)}
        />
      );
    }

    // Fallback to initials
    const initials = name
      ? name
          .split(" ")
          .map((n) => n[0])
          .join("")
          .toUpperCase()
      : "";

    return (
      <div className="w-[110px] h-[110px] rounded-full bg-gradient-to-br from-[#5e78f4] to-[#f14ce2] flex items-center justify-center text-white text-[32px] font-bold !p-0">
        {initials}
      </div>
    );
  };

  //social icons and link component
  const SocialLink = ({ platform, socialObj, subscription_type }) => {
    const url =
      socialObj?.social?.[platform] ||
      socialObj?.social?.[platform + "Url"] ||
      socialObj?.[platform] ||
      socialObj?.[platform + "Url"];
    if (!url || url == "false") return null;

    const isCompany = socialObj?.name;

    // If should hide data and not company, show tooltip and handle click
    if (subscription_type != "paid" && !isCompany) {
      return (
        <Tooltip
          content="Please upgrade to open social profiles"
          trigger="hover"
          style="light"
          placement="top"
        >
          <div
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsUpgradeModalOpen(true);
            }}
            className="blur-sm cursor-pointer"
          >
            <Icon name={platform} className="w-5 h-5 text-[#2a2a2a]" />
          </div>
        </Tooltip>
      );
    }

    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:opacity-75 transition-opacity"
      >
        <Icon name={platform} className="w-5 h-5 text-[#2a2a2a]" />
      </a>
    );
  };

  // Commented out - now using common DetailsDrawer component
  // Wrapped in if(false) to keep code but prevent execution
  if (false) {
  const DetailsDrawer_OLD = () => {
    const [activeDrawerTab, setActiveDrawerTab] = useState("visitor");

    // Helper function to scroll to section
    const scrollToSection = (sectionId) => {
      setActiveDrawerTab(sectionId);
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    };

    // Sample data - in real app this would come from drawerData prop
    const visitorData = drawerData?.exactPerson || {};

    const companyData = drawerData?.company || {};

    // Skeleton loader component
    const SkeletonLoader = () => (
      <div>
        <style>{`
          @keyframes shimmer {
            0% {
              background-position: -1000px 0;
            }
            100% {
              background-position: 1000px 0;
            }
          }
          .skeleton-wave {
            animation: shimmer 2s infinite linear;
            background: linear-gradient(
              to right,
              #f0f0f0 0%,
              #e0e0e0 20%,
              #f0f0f0 40%,
              #f0f0f0 100%
            );
            background-size: 1000px 100%;
          }
        `}</style>
        {/* Visitor Section Skeleton */}
        <section className="mb-[50px]">
          <div className="flex items-center gap-2.5 mb-[30px]">
            <div className="w-[15px] h-5 bg-gray-300 rounded skeleton-wave"></div>
            <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
          </div>
          <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
            <div className="flex items-center mb-[30px] gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
              {/* Avatar skeleton */}
              <div className="w-[110px] h-[110px] rounded-full bg-gray-300 skeleton-wave"></div>

              {/* Basic info skeleton */}
              <div className="flex flex-col gap-2.5">
                <div className="h-6 w-48 bg-gray-300 rounded skeleton-wave"></div>
                <div className="flex items-center gap-[8px]">
                  <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                </div>
              </div>

              {/* Additional info skeletons */}
              <div className="flex flex-col gap-2.5">
                <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
              </div>
              <div className="flex flex-col gap-2.5">
                <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                <div className="h-6 w-40 bg-gray-300 rounded skeleton-wave"></div>
              </div>
              <div className="flex flex-col gap-2.5">
                <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                <div className="h-6 w-36 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

        {/* Company Section Skeleton */}
        <section className="mb-[50px]">
          <div className="flex items-center gap-2.5 mb-[30px]">
            <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
            <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
          </div>
          <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
            <div className="flex items-center mb-[30px] gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
              <div className="w-[110px] h-[110px] rounded-full bg-gray-300 skeleton-wave"></div>
              <div className="flex flex-col gap-2.5 flex-1">
                <div className="h-6 w-48 bg-gray-300 rounded skeleton-wave"></div>
                <div className="flex items-center gap-[30px]">
                  <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                  <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
                </div>
              </div>
              <div className="flex flex-col gap-2.5">
                <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Divider */}
        <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

        {/* Page Visits Section Skeleton */}
        <section className="mb-[50px]">
          <div className="flex items-center gap-2.5 mb-[30px]">
            <div className="w-5 h-5 bg-gray-300 rounded skeleton-wave"></div>
            <div className="h-6 w-32 bg-gray-300 rounded skeleton-wave"></div>
          </div>
          <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="bg-white border border-[#e9ebf0] rounded-[20px] p-5"
                >
                  <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex flex-col gap-2 flex-1">
                      <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="h-6 w-64 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="h-5 w-20 bg-gray-300 rounded skeleton-wave"></div>
                      <div className="h-6 w-16 bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    );

    return (
      <Drawer
        open={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);
          setDrawerData(null);
        }}
        position="right"
        className="p-0 w-4/5 notification-canvas"
      >
        <div className="h-full flex flex-col bg-white">
          {/* Header with Tabs */}
          <div className="sticky flex top-0 z-10 bg-white border-b border-[#dcdcdc] h-[90px] items-center">
            {/* Tab Navigation */}
            <div className="flex flex-1 items-center h-full">
              {/* Visitor Tab */}
              <button
                onClick={() => scrollToSection("visitor")}
                className={`flex items-center gap-2.5 h-full px-10 ${
                  activeDrawerTab === "visitor"
                    ? "text-[#097ba9] bg-[#E9F2FF]"
                    : "text-[#2a2a2a]"
                } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
              >
                <Icon name="drawer_visitor" className="w-4 h-4" />
                <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                  Visitor
                </span>
              </button>

              {/* Company Tab */}
              <button
                onClick={() => scrollToSection("company")}
                className={`flex items-center gap-2.5 h-full px-10 ${
                  activeDrawerTab === "company"
                    ? "text-[#097ba9] bg-[#E9F2FF]"
                    : "text-[#2a2a2a]"
                } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
              >
                <Icon name="company" className="w-4 h-4" />
                <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                  Company
                </span>
              </button>

              {/* Page Visits Tab */}
              <button
                onClick={() => scrollToSection("pageVisits")}
                className={`flex items-center gap-2.5 h-full px-10 ${
                  activeDrawerTab === "pageVisits"
                    ? "text-[#097ba9] bg-[#E9F2FF]"
                    : "text-[#2a2a2a]"
                } hover:text-[#097ba9] hover:bg-[#E9F2FF] transition-colors duration-200`}
              >
                <Icon name="visits" className="w-4 h-4" />
                <span className="font-['Nunito:Bold',_sans-serif] font-bold text-[18px]">
                  Page Visits
                </span>
              </button>
            </div>
            {/* Close Button */}
            <button
              onClick={() => {
                setIsDrawerOpen(false);
                setDrawerData(null);
              }}
              className=" w-10 h-10 flex items-center justify-center text-[#2a2a2a] hover:bg-[#f0f0f0] rounded-full transition-colors duration-200 mr-2"
            >
              <Icon name="close" className="w-10 h-10" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-auto px-[60px] py-[40px]">
            {drawerLoading ? (
              <SkeletonLoader />
            ) : (
              <>
                {/* Visitor Section */}
                <section id="visitor" className="mb-[50px]">
                  <div className="flex items-center gap-2.5 mb-[30px]">
                    <Icon
                      name="drawer_visitor"
                      className="w-[15px] h-5 text-[#000000]"
                    />
                    <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                      Visitors
                    </h2>
                  </div>

                  <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                    <div className="flex  items-center mb-[30px]  gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
                      {/* User Avatar */}
                      <UserAvatar
                        name={
                          [visitorData?.firstName, visitorData?.lastName]
                            .filter(Boolean)
                            .join(" ") || ""
                        }
                        url={visitorData?.picture}
                      />

                      {/* Basic Info */}
                      <div className="flex flex-col gap-2.5 ">
                        <h3
                          style={{
                            display:
                              visitorData?.firstName || visitorData?.lastName
                                ? "block"
                                : "none",
                          }}
                          className="font-bold font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#2a2a2a]"
                        >
                          {[visitorData?.firstName, visitorData?.lastName]
                            .filter(Boolean)
                            .join(" ") || <>&nbsp;</>}
                        </h3>
                        <div className="flex items-center gap-[8px]">
                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="linkedin"
                            socialObj={visitorData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="twitter"
                            socialObj={visitorData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="facebook"
                            socialObj={visitorData}
                          />
                        </div>
                      </div>

                      {/* ICP Score */}
                      <div
                        style={{
                          display: drawerData?.icpScore ? "block" : "none",
                        }}
                        className="flex flex-col gap-[5px] "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="icp_score"
                            className="w-6 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            ICP Score
                          </span>
                        </div>
                        {drawerData?.subscription_type != "paid" ? (
                          <Tooltip
                            trigger="hover"
                            autoFocus={true}
                            style={"light"}
                            content="Please upgrade to view ICP Score details"
                          >
                            <div
                              onClick={() => setIsUpgradeModalOpen(true)}
                              className={`flex items-center gap-2.5 blur-sm cursor-pointer`}
                            >
                              <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                                ICP&nbsp;
                              </span>

                              <Icon
                                name="information"
                                className="w-5 h-5 hidden"
                              />
                            </div>
                          </Tooltip>
                        ) : (
                          <div className={`flex items-center gap-2.5`}>
                            <span className="font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#ff6a00] w-[27px]">
                              {drawerData?.icpScore || <>&nbsp;</>}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Title */}
                      <div
                        style={{
                          display: visitorData?.position ? "block" : "none",
                        }}
                        className="flex items-center "
                      >
                        <div className="flex flex-col gap-2.5 flex-1">
                          <div className="flex items-center gap-2.5">
                            <Icon
                              name="drawer_title"
                              className="w-[23px] h-5 text-[#2a2a2a]"
                            />
                            <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                              Title
                            </span>
                          </div>
                          <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                            {visitorData?.position || <>&nbsp;</>}
                          </span>
                        </div>
                      </div>

                      {/* Email */}
                      <div
                        style={{
                          display:
                            visitorData?.businessEmail ||
                            visitorData?.personalEmail
                              ? "block"
                              : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="email"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Email Address
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {[
                            visitorData?.businessEmail ||
                              visitorData?.personalEmail,
                          ]
                            .filter(Boolean)
                            .map((email, idx) =>
                              drawerData?.subscription_type != "paid" ? (
                                <>
                                  <Tooltip
                                    content="Please upgrade to view email"
                                    trigger="hover"
                                    style="light"
                                    placement="top"
                                  >
                                    <div
                                      onClick={() =>
                                        setIsUpgradeModalOpen(true)
                                      }
                                      className="blur-sm cursor-pointer"
                                    >
                                      <p
                                        key={idx}
                                        className=" text-[#2a2a2a] font-normal underline"
                                      >
                                        placeholder@email.test
                                      </p>
                                    </div>
                                    {idx < 1 && <br />}
                                  </Tooltip>
                                </>
                              ) : (
                                <>
                                  <a
                                    key={idx}
                                    href={`mailto:${email}`}
                                    className=" text-[#2a2a2a] font-normal underline"
                                  >
                                    {email}
                                  </a>
                                  {idx < 1 && <br />}
                                </>
                              )
                            )}
                        </span>
                      </div>

                      {/* Phone */}
                      <div
                        style={{
                          display: visitorData?.phone ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="phone"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Phone
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {drawerData?.subscription_type != "paid" ? (
                            <>
                              <Tooltip
                                content="Please upgrade to view phone number"
                                trigger="hover"
                                style="light"
                                placement="top"
                              >
                                <div
                                  onClick={() => setIsUpgradeModalOpen(true)}
                                  className="blur-sm cursor-pointer"
                                >
                                  <p className=" text-[#2a2a2a] font-normal underline">
                                    +1 (555) 555-5555
                                  </p>
                                </div>
                              </Tooltip>
                            </>
                          ) : (
                            <>
                              <a
                                href={`tel:${
                                  visitorData?.phone
                                    ? "+" + visitorData?.phone
                                    : ""
                                }`}
                                className=" text-[#2a2a2a] font-normal underline"
                              >
                                {visitorData?.phone
                                  ? "+" + visitorData?.phone
                                  : ""}
                              </a>
                            </>
                          )}
                        </span>
                      </div>

                      {/* Last Seen */}
                      <div
                        style={{
                          display: drawerData?.lastSession?.date
                            ? "block"
                            : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_clock"
                            className="w-[33px] h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Last Seen
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {drawerData?.lastSession?.date ? (
                            // moment(drawerData?.lastSession?.date).format(
                            //   "MMMM Do YYYY, h:mm:ss a"
                            // )
                            moment(drawerData.lastSession.date)
                              .utc()
                              .format("MMMM Do YYYY, h:mm:ss a") + " UTC"
                          ) : (
                            <>&nbsp;</>
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>
                {/* Divider Line */}
                <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

                {/* Company Section */}
                <section id="company" className="mb-[50px]">
                  <div className="flex items-center gap-2.5 mb-[30px]">
                    <Icon name="company" className="w-5 h-5 text-[#000000]" />
                    <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                      Company
                    </h2>
                  </div>

                  <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                    <div className="flex  items-center mb-[30px]  gap-y-12 gap-x-6 flex-wrap divide-x-2 [&>*:not(:first-child)]:pl-4 [&>*]:pr-4">
                      <UserAvatar
                        name={companyData?.name || ""}
                        url={companyData?.logo || ""}
                      />
                      <div className="flex flex-col gap-2.5 flex-1">
                        <h3
                          style={{
                            display: companyData?.name ? "block" : "none",
                          }}
                          className="font-bold font-['Axiforma:Bold',_sans-serif] text-[20px] text-[#2a2a2a]"
                        >
                          {companyData?.name || <>&nbsp;</>}
                        </h3>
                        <div className="flex items-center gap-[30px]">
                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="linkedin"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="twitter"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="youtube"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="crunchbase"
                            socialObj={companyData}
                          />

                          <SocialLink
                            subscription_type={drawerData?.subscription_type}
                            platform="facebook"
                            socialObj={companyData}
                          />
                        </div>
                      </div>
                      <div
                        style={{
                          display: companyData?.sector ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 px-2.5"
                      >
                        <div className="flex items-center gap-[10px]">
                          <Icon
                            name="drawer_sector"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Sector
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.sector || <>&nbsp;</>}
                        </span>
                      </div>
                      <div
                        style={{
                          display:
                            companyData?.city ||
                            companyData?.state ||
                            companyData?.country
                              ? "block"
                              : "none",
                        }}
                        className="flex flex-col gap-2.5"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_location"
                            className="w-4 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Location
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {[
                            companyData?.city,
                            companyData?.state,
                            companyData?.country,
                          ]
                            ?.filter(Boolean)
                            .join(", ") || <>&nbsp;</>}
                        </span>
                      </div>
                      <div
                        style={{
                          display: companyData?.yearFounded ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_year"
                            className="w-[19px] h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Year Founded
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.yearFounded || <>&nbsp;</>}
                        </span>
                      </div>
                      <div
                        style={{
                          display: companyData?.employeesRange
                            ? "block"
                            : "none",
                        }}
                        className="flex flex-col gap-2.5 "
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_employees"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Total Employee
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.employeesRange || <>&nbsp;</>}
                        </span>
                      </div>
                      <div
                        style={{
                          display: companyData?.revenueRange ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_revenue"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Revenue
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.revenueRange || <>&nbsp;</>}
                        </span>
                      </div>
                      <div
                        style={{
                          display: companyData?.industry ? "block" : "none",
                        }}
                        className="flex flex-col gap-2.5 w-[124px]"
                      >
                        <div className="flex items-center gap-2.5">
                          <Icon
                            name="drawer_industry"
                            className="w-5 h-5 text-[#2a2a2a]"
                          />
                          <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[18px] text-[#2a2a2a]">
                            Industry
                          </span>
                        </div>
                        <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                          {companyData?.industry || <>&nbsp;</>}
                        </span>
                      </div>
                    </div>
                  </div>
                </section>

                {/* Divider Line */}
                <div className="w-full h-[1px] bg-[#dcdcdc] mb-[50px]"></div>

                {/* Page Visits Section */}
                <section id="pageVisits" className="mb-[50px]">
                  <div className="flex items-center gap-2.5 mb-[30px]">
                    <Icon name="visits" className="w-5 h-5 text-[#000000]" />
                    <h2 className="font-bold font-['Axiforma:Bold',_sans-serif] text-[24px] text-[#000000]">
                      Page Visits
                    </h2>
                  </div>

                  <div className="bg-[#fafbfc] border border-[#dcdcdc] rounded-[30px] p-[30px]">
                    {drawerData?.pageVisits &&
                    drawerData.pageVisits.length > 0 ? (
                      <div className="space-y-4">
                        {drawerData?.pageVisits.map((visit, index) => (
                          <div
                            key={index}
                            className="bg-white border border-[#e9ebf0] rounded-[20px] p-5 hover:shadow-md transition-shadow duration-200"
                          >
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                              {/* Path */}
                              <div className="flex flex-col gap-2 flex-1 min-w-[200px]">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    name="globe"
                                    className="w-4 h-4 text-[#2a2a2a]"
                                  />
                                  <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                    Page Path
                                  </span>
                                </div>
                                <span className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#097ba9] break-all">
                                  {visit?.path?.includes("http")
                                    ? visit?.path
                                    : drawerData?.workspaceName + visit?.path}
                                </span>
                              </div>

                              {/* Visit Count */}
                              <div className="flex flex-col gap-2 min-w-[120px]">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    name="visits"
                                    className="w-4 h-4 text-[#2a2a2a]"
                                  />
                                  <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                    Visit Count
                                  </span>
                                </div>
                                <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                  {visit.visitCount || 0}
                                </span>
                              </div>

                              {/* Total Time */}
                              <div className="flex flex-col gap-2 min-w-[120px]">
                                <div className="flex items-center gap-2">
                                  <Icon
                                    name="duration"
                                    className="w-4 h-4 text-[#2a2a2a]"
                                  />
                                  <span className="font-bold font-['Axiforma:Bold',_sans-serif] text-[16px] text-[#2a2a2a]">
                                    Total Time
                                  </span>
                                </div>
                                <span className="font-['Axiforma:Regular',_sans-serif] text-[20px] text-[#2a2a2a]">
                                  {formatDuration(visit.totalTime || 0)}
                                </span>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-[50px]">
                        <p className="font-['Axiforma:Regular',_sans-serif] text-[18px] text-[#666] mb-4">
                          No page visits recorded
                        </p>
                        <p className="font-['Axiforma:Regular',_sans-serif] text-[14px] text-[#999]">
                          Page visit data will appear here once the visitor
                          browses your website.
                        </p>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}
          </div>
        </div>
      </Drawer>
    );
  };
  }

  if (loading) {
    return (
      <div className="bg-white flex min-h-screen">
        <main className="flex-1 p-8">
          {/* Header Skeleton */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-6 h-6 bg-gray-300 rounded skeleton-wave"></div>
              <div>
                <div className="h-7 w-32 bg-gray-300 rounded skeleton-wave mb-2"></div>
                <div className="h-5 w-48 bg-gray-300 rounded skeleton-wave"></div>
              </div>
            </div>
          </div>

          {/* Tabs Skeleton */}
          <div className="flex border-b border-[#E5E7EB] mb-6">
            <div className="h-12 w-32 bg-gray-300 rounded-t skeleton-wave"></div>
          </div>

          {/* Content Skeleton */}
          <div className="mb-6 flex justify-between items-center">
            <div className="h-10 w-32 bg-gray-300 rounded skeleton-wave"></div>
            <div className="flex items-center gap-3">
              <div className="h-10 w-24 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-10 w-10 bg-gray-300 rounded skeleton-wave"></div>
              <div className="h-10 w-10 bg-gray-300 rounded skeleton-wave"></div>
            </div>
          </div>

          {/* Table Skeleton */}
          <div className="border border-[#E5E7EB] rounded-lg overflow-hidden">
            {/* Table Header Skeleton */}
            <div className="bg-[#F9FAFB] border-b border-[#E5E7EB] p-4">
              <div className="flex gap-4">
                {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                  <div key={col} className="flex-1">
                    <div className="h-5 w-24 bg-gray-300 rounded skeleton-wave"></div>
                  </div>
                ))}
              </div>
            </div>

            {/* Table Rows Skeleton */}
            {[1, 2, 3, 4, 5].map((row) => (
              <div key={row} className="border-b border-[#E5E7EB] p-4">
                <div className="flex gap-4 items-center">
                  {[1, 2, 3, 4, 5, 6, 7].map((col) => (
                    <div key={col} className="flex-1">
                      <div className="h-5 w-full bg-gray-300 rounded skeleton-wave"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white flex  min-h-screen">
        <main className="flex-1 p-8">
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="text-[#DC2626] text-[16px] font-[500] mb-2">
                Error Loading Campaigns
              </div>
              <div className="text-[#6B7280] text-[14px] mb-4">{error}</div>
              <button
                onClick={fetchCampaignsData}
                className="py-2 px-4 rounded-[15px] font-[500] text-[14px] bg-[#5E78F4] text-white hover:bg-[#4A63D2] transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const inboxColumns = [
    { key: "lead", title: "Lead Email", highlighted: false },
    { key: "email1", title: "Email 1", highlighted: false },
    { key: "replay1", title: "Reply", highlighted: false },
    { key: "email2", title: "Email 2", highlighted: false },
    { key: "replay2", title: "Reply", highlighted: false },
    { key: "email3", title: "Email 3", highlighted: false },
    { key: "replay3", title: "Reply", highlighted: false },
  ];

  return (
    <div className="bg-white flex  ">
      <main className="flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/email-outreach"
              className="text-[#6B7280] hover:text-[#374151]"
            >
              <ArrowLeft className="w-6 h-6" />
            </Link>
            <div>
              <h1 className="font-[700] text-[#2A2A2A] text-[24px]">
                Email Inbox
              </h1>
              {provider && (
                <p className="text-[#6B7280] text-[14px] mt-1">
                  {provider.providerEmail} •{" "}
                  {provider.providerType.toUpperCase()}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-[#E5E7EB] mb-6">
          <button
            onClick={() => setActiveTab("inbox")}
            className={`px-6 py-3 font-[500] text-[16px] border-b-2 transition-colors ${
              activeTab === "inbox"
                ? "border-[#5E78F4] text-[#5E78F4]"
                : "border-transparent text-[#6B7280] hover:text-[#374151]"
            }`}
          >
            Inbox (
            {leadsEmails?.reduce(
              (acc, email) => acc + email?.eamil?.length,
              0
            ) || 0}
            )
          </button>
          {/* <button
            onClick={() => setActiveTab("leads")}
            className={`px-6 py-3 font-[500] text-[16px] border-b-2 transition-colors ${
              activeTab === "leads"
                ? "border-[#5E78F4] text-[#5E78F4]"
                : "border-transparent text-[#6B7280] hover:text-[#374151]"
            }`}
          >
            Leads ({leads.length})
          </button> */}
        </div>

        {/* Content */}
        {activeTab === "inbox" ? (
          <div>
            {/* Campaign Dropdown and Timeline Controls */}
            <div className="mb-6 flex justify-between items-center">
              <div className="relative">
                {/* <select
                  className="appearance-none bg-white border border-[#E5E7EB] rounded-[25px] px-6 py-2 pr-10 text-[14px] font-[500] text-[#374151] focus:outline-none focus:border-[#5E78F4] cursor-pointer"
                  value={selectedCampaign}
                  onChange={(e) => {
                    setSelectedCampaign(e.target.value);
                    // Optional: Log selected campaign data for debugging
                    if (e.target.value) {
                      const selectedData = campaigns.find(
                        (c) => c.id.toString() === e.target.value
                      );
                      clog("Selected campaign:", selectedData);
                    }
                  }}
                  disabled={campaigns.length === 0}
                >
                  {campaigns.length === 0 ? (
                    <option value="">No campaigns available</option>
                  ) : (
                    <>
                      <option value="">Select Campaign</option>
                      {campaigns.map((campaign) => (
                        <option
                          key={campaign.id}
                          value={campaign.id.toString()}
                        >
                          {campaign.name}
                        </option>
                      ))}
                    </>
                  )}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6B7280] pointer-events-none" /> */}
              </div>

              {/* Timeline Controls */}
              <div className="flex items-center gap-3">
                {/* Active and Completed dropdown filter */}
                <select
                  className="px-4 py-2 text-[12px] font-[500] text-[#374151] bg-white border border-[#E5E7EB] rounded-[12px] hover:bg-[#F3F4F6] transition-colors"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="active">Active</option>
                  <option value="completed">Completed</option>
                </select>
                {/* <button
                  onClick={
                    allRowsCollapsed
                      ? expandAllRows
                      : () => collapseAllRows(leadsEmails)
                  }
                  className="px-4 py-3 text-[12px] font-[500] text-[#374151] bg-white border border-[#E5E7EB] rounded-[12px] hover:bg-[#F3F4F6] transition-colors"
                  disabled={!leadsEmails || leadsEmails.length === 0}
                >
                  {allRowsCollapsed ? (
                    <>
                      <Maximize2 className="w-4 h-4 inline mr-2" />
                      Expand All
                    </>
                  ) : (
                    <>
                      <Minimize2 className="w-4 h-4 inline mr-2" />
                      Collapse All
                    </>
                  )}
                </button> */}
                {/* expand all */}
                <Tooltip content="Expand All" placement="top">
                  <button
                    onClick={() => {
                      setAllRowsCollapsed(false);
                      expandAllRows(leadsEmails);
                    }}
                    className="px-4 py-3 text-[12px] font-[500] text-[#374151] bg-white border border-[#E5E7EB] rounded-[12px] hover:bg-[#F3F4F6] transition-colors"
                    disabled={!leadsEmails || leadsEmails.length === 0}
                  >
                    <Maximize2 className="w-4 h-4 inline" />
                  </button>
                </Tooltip>
                {/* collapse all */}
                <Tooltip content="Collapse All" placement="top">
                  <button
                    onClick={() => {
                      setAllRowsCollapsed(true);
                      collapseAllRows(leadsEmails);
                    }}
                    className="px-4 py-3 text-[12px] font-[500] text-[#374151] bg-white border border-[#E5E7EB] rounded-[12px] hover:bg-[#F3F4F6] transition-colors"
                    disabled={!leadsEmails || leadsEmails.length === 0}
                  >
                    <Minimize2 className="w-4 h-4 inline" />
                  </button>
                </Tooltip>
                <div className="flex items-center bg-white border border-[#E5E7EB] rounded-[12px] p-1">
                  <button
                    onClick={() =>
                      setTimelineZoom(Math.max(0.5, timelineZoom - 0.25))
                    }
                    className="p-2 hover:bg-[#F3F4F6] rounded-[8px] transition-colors"
                    title="Zoom Out"
                  >
                    <ZoomOut className="w-4 h-4 text-[#6B7280]" />
                  </button>
                  <div className="px-3 py-1 text-[12px] font-[500] text-[#374151] min-w-[60px] text-center">
                    {Math.round(timelineZoom * 100)}%
                  </div>
                  <button
                    onClick={() =>
                      setTimelineZoom(Math.min(2, timelineZoom + 0.25))
                    }
                    className="p-2 hover:bg-[#F3F4F6] rounded-[8px] transition-colors"
                    title="Zoom In"
                  >
                    <ZoomIn className="w-4 h-4 text-[#6B7280]" />
                  </button>
                </div>

                <button
                  onClick={() => setTimelineZoom(1)}
                  className="p-2 bg-white border border-[#E5E7EB] rounded-[12px] hover:bg-[#F3F4F6] transition-colors"
                  title="Reset Zoom"
                >
                  <RotateCcw className="w-4 h-4 text-[#6B7280]" />
                </button>

                {/* <button
                  onClick={() => setIsTimelineExpanded(!isTimelineExpanded)}
                  className="p-2 bg-white border border-[#E5E7EB] rounded-[12px] hover:bg-[#F3F4F6] transition-colors"
                  title={
                    isTimelineExpanded ? "Collapse Timeline" : "Expand Timeline"
                  }
                >
                  {isTimelineExpanded ? (
                    <Minimize2 className="w-4 h-4 text-[#6B7280]" />
                  ) : (
                    <Maximize2 className="w-4 h-4 text-[#6B7280]" />
                  )}
                </button> */}
              </div>
            </div>

            {/* Interactive Email Timeline Table */}
            <div className={` overflow-auto max-h-[60vh]`}>
              <table
                style={{
                  transform: `scale(${timelineZoom})`,
                  transformOrigin: "top left",
                }}
                className="w-full max-w-full transition-all duration-300  bg-white rounded-[15px] border border-[#E5E7EB]"
              >
                <thead
                  className={`${
                    timelineZoom <= 1 && "sticky top-0"
                  } z-10 bg-white shadow-sm`}
                >
                  <tr className="border-b border-[#E5E7EB]">
                    {inboxColumns.map((column) => {
                      const isCollapsed = collapsedColumns.has(column.key);
                      return (
                        <th
                          key={column.key}
                          className={`px-4 py-3 text-left font-[600] text-[14px] transition-all duration-300 bg-white border-b border-[#E5E7EB] ${
                            isCollapsed ? "max-w-[60px] " : "max-w-[200px] "
                          } ${
                            column.highlighted
                              ? "text-white bg-gradient-to-br from-[#8B5CF6] to-[#A855F7]"
                              : "text-[#374151]"
                          }`}
                        >
                          <div className="flex items-center justify-between">
                            <span className={isCollapsed ? "truncate" : ""}>
                              {isCollapsed
                                ? column.title.charAt(0)
                                : column.title}
                            </span>
                            {column.title != "Lead Email" && (
                              <button
                                onClick={() => {
                                  const newCollapsed = new Set(
                                    collapsedColumns
                                  );
                                  if (isCollapsed) {
                                    newCollapsed.delete(column.key);
                                  } else {
                                    newCollapsed.add(column.key);
                                  }
                                  setCollapsedColumns(newCollapsed);
                                }}
                                className={`ml-2 p-1 rounded-[4px] transition-colors ${
                                  column.highlighted
                                    ? "hover:bg-white/20"
                                    : "hover:bg-[#F3F4F6]"
                                }`}
                                title={
                                  isCollapsed
                                    ? "Expand Column"
                                    : "Collapse Column"
                                }
                              >
                                {isCollapsed ? (
                                  <ChevronUp className="w-3 h-3" />
                                ) : (
                                  <ChevronDown className="w-3 h-3" />
                                )}
                              </button>
                            )}
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                </thead>
                <tbody>
                  {/* Lead 1 - Milan Rodriguez */}
                  {leadsEmails
                    .filter((lead) => {
                      if (statusFilter === "active") {
                        return lead.emailsSent < 3;
                      } else if (statusFilter === "completed") {
                        return lead.emailsSent === 3;
                      }
                      return true;
                    })
                    .map((lead) => (
                      <React.Fragment key={lead.id || lead.providerId}>
                        {collapsedRows[lead.id] ? (
                          <tr className="border-b border-[#F3F4F6] relative">
                            {/* Lead Email Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 relative ${
                                collapsedColumns.has("lead")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              <div className="absolute -left-2 h-full bottom-[50%] translate-y-[50%] inline-flex justify-center items-center">
                                <div
                                  onClick={() => toggleRowCollapse(lead.id)}
                                  className="inline-flex group items-center gap-2 bg-[#E5E7EB] text-[#374151] text-[12px] px-4 py-2 rounded-r-full cursor-pointer opacity-50 hover:opacity-100 transition-all duration-300"
                                >
                                  <Maximize2 className="w-4 h-4" />
                                  <span className="hidden group-hover:inline-block">
                                    Click to expand
                                  </span>
                                </div>
                              </div>
                              <div className="ms-6">
                                <div
                                  className="text-[#374151] text-[11px] font-[500] mb-1 truncate cursor-pointer hover:text-[#5E78F4] transition-colors"
                                  onClick={() => fetchLeadDetails(lead?.email)}
                                >
                                  {lead?.firstName} {lead?.lastName}
                                </div>
                                <div className="text-[#5E78F4] text-[10px] font-[500] truncate">
                                  {lead?.email}
                                </div>
                              </div>
                            </td>

                            {/* Email 1 Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("email1")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              {!collapsedColumns.has("email1") ? (
                                <div className="text-[#6B7280] text-[10px]">
                                  Email 1
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Email 1
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* Reply 1 Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("replay1")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              {!collapsedColumns.has("replay1") ? (
                                <div className="text-[#6B7280] text-[10px]">
                                  Reply
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Reply
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* Email 2 Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("email2")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              {!collapsedColumns.has("email2") ? (
                                <div className="text-[#6B7280] text-[10px]">
                                  Email 2
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Email 2
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* Reply 2 Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("replay2")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              {!collapsedColumns.has("replay2") ? (
                                <div className="text-[#6B7280] text-[10px]">
                                  Reply
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Reply
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* Email 3 Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("email3")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              {!collapsedColumns.has("email3") ? (
                                <div className="text-[#6B7280] text-[10px]">
                                  Email 3
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Email 3
                                  </div>
                                </div>
                              )}
                            </td>

                            {/* Reply 3 Column - Collapsed */}
                            <td
                              className={`px-4 py-4 align-top transition-all duration-300 ${
                                collapsedColumns.has("replay3")
                                  ? "max-w-[60px] min-h-[120px]"
                                  : "max-w-[200px]"
                              }`}
                            >
                              {!collapsedColumns.has("replay3") ? (
                                <div className="text-[#6B7280] text-[10px]">
                                  Reply
                                </div>
                              ) : (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Reply
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        ) : (
                          <tr className="border-b border-[#F3F4F6] min-h-[200px] relative">
                            {/* Lead Email */}
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("lead")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {/* make the button vertically center position */}
                              <div className="absolute -left-2  h-full bottom-[50%] translate-y-[50%] inline-flex justify-center items-center  ">
                                <div
                                  onClick={() => toggleRowCollapse(lead.id)}
                                  className="inline-flex items-center group gap-2 bg-[#E5E7EB] text-[#374151] text-[12px] px-4 py-2 rounded-r-full  cursor-pointer opacity-50 hover:opacity-100  transition-all duration-300"
                                >
                                  <Minimize2 className="w-4 h-4 " />
                                  <span className="hidden group-hover:inline-block">
                                    Click to collapse
                                  </span>
                                </div>
                              </div>
                              {!collapsedColumns.has("lead") && (
                                <div className="ms-6">
                                  <div className="mb-3">
                                    <div
                                      className="text-[#374151] text-[13px] font-[600] mb-1 cursor-pointer hover:text-[#5E78F4] transition-colors"
                                      onClick={() =>
                                        fetchLeadDetails(lead?.email)
                                      }
                                    >
                                      {lead?.firstName} {lead?.lastName}
                                    </div>
                                    <div className="text-[#5E78F4] text-[11px] font-[500] mb-1">
                                      {lead?.email}
                                    </div>
                                    <div className="text-[#6B7280] text-[10px]">
                                      {lead?.company}
                                    </div>
                                  </div>
                                  <div className="mb-3">
                                    {/* <div className="inline-flex items-center gap-1 bg-[#DBEAFE] text-[#1E40AF] text-[10px] px-2 py-1 rounded-[6px] mb-2">
                                <div className="w-1.5 h-1.5 bg-[#1E40AF] rounded-full"></div>
                                Active Lead
                              </div> */}
                                    <div className="space-y-1 text-[10px] text-[#6B7280]">
                                      {/* {lead?.icp?.icp_score && (
                                      <div>
                                        <span className="font-[500]">
                                          ICP Score:
                                        </span>{" "}
                                        {lead?.icp?.icp_score}/100
                                      </div>
                                    )} */}
                                      {lead?.eamil[lead?.emailsSent - 1]?.pis
                                        ?.pis_score && (
                                        <div>
                                          <span className="font-[500]">
                                            PIS Score:
                                          </span>{" "}
                                          {
                                            lead?.eamil[lead?.emailsSent - 1]
                                              ?.pis?.pis_score
                                          }
                                          /100
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                  <div className="space-y-1 text-[10px] text-[#6B7280]">
                                    {lead?.icp?.webhook_data?.contact
                                      ?.position && (
                                      <div>
                                        <span className="font-[500]">
                                          Title:
                                        </span>{" "}
                                        {
                                          lead?.icp?.webhook_data?.contact
                                            ?.position
                                        }
                                      </div>
                                    )}
                                    {lead?.icp?.webhook_data?.company
                                      ?.industry && (
                                      <div>
                                        <span className="font-[500]">
                                          Industry:
                                        </span>{" "}
                                        {
                                          lead?.icp?.webhook_data?.company
                                            ?.industry
                                        }
                                      </div>
                                    )}
                                    {lead?.icp?.webhook_data?.company
                                      ?.employeesRange && (
                                      <div>
                                        <span className="font-[500]">
                                          Company Size:
                                        </span>{" "}
                                        {
                                          lead?.icp?.webhook_data?.company
                                            ?.employeesRange
                                        }
                                      </div>
                                    )}
                                    {lead?.icp?.webhook_data?.contact?.geo
                                      ?.fullAddress && (
                                      <div>
                                        <span className="font-[500]">
                                          Location:
                                        </span>{" "}
                                        {
                                          lead?.icp?.webhook_data?.contact?.geo
                                            ?.fullAddress
                                        }
                                      </div>
                                    )}
                                  </div>

                                  {/* Handoff Status */}
                                  {/* <div className="mt-3 pt-2 border-t border-[#E5E7EB]">
                              <div className="flex items-center gap-2 mb-2">
                                <div className="w-2 h-2 bg-[#10B981] rounded-full animate-pulse"></div>
                                <div className="text-[#10B981] text-[10px] font-[600]">
                                  HANDED OFF TO SALES
                                </div>
                              </div>
                              <div className="bg-[#ECFDF5] border border-[#10B981]/20 rounded-[6px] p-2 mb-2">
                                <div className="text-[#065F46] text-[9px] font-[500] mb-1">
                                  Assigned to: Sarah Martinez
                                </div>
                                <div className="text-[#065F46] text-[8px]">
                                  Reason: Positive engagement detected
                                </div>
                                <div className="text-[#065F46] text-[8px]">
                                  Handoff: 2 hours ago
                                </div>
                              </div>
                              <div className="text-[#374151] text-[10px] font-[500] mb-1">
                                Next Action
                              </div>
                              <div className="text-[9px] text-[#6B7280]">
                                • Conduct demo Tuesday 2 PM PST
                              </div>
                              <div className="text-[9px] text-[#6B7280]">
                                • Share ROI calculator
                              </div>
                            </div> */}
                                </div>
                              )}
                              {collapsedColumns.has("lead") && (
                                <div className="ms-6">
                                  <div
                                    className="text-[#374151] text-[11px] font-[500] mb-1 truncate cursor-pointer hover:text-[#5E78F4] transition-colors"
                                    onClick={() =>
                                      fetchLeadDetails(lead?.email)
                                    }
                                  >
                                    {lead?.firstName} {lead?.lastName}
                                  </div>
                                  <div className="text-[#5E78F4] text-[10px] font-[500] truncate">
                                    {lead?.email}
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("email1")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {!collapsedColumns.has("email1") &&
                                (lead?.eamil[0] ? (
                                  <div className="space-y-3">
                                    <div className="text-[#6B7280] text-[11px] leading-relaxed">
                                      {lead?.eamil[0]?.subject && (
                                        <p className="font-bold">
                                          Subject :{" "}
                                          {lead?.eamil[0]?.subject || ""}
                                        </p>
                                      )}
                                      {lead?.eamil[0]?.content || ""}
                                    </div>

                                    {/* Email Analytics */}
                                    <div className="flex flex-wrap gap-2">
                                      {/* <div className="inline-flex items-center gap-1 bg-[#ECFDF5] text-[#059669] text-[9px] px-2 py-1 rounded-[4px]">
                                <div className="w-1 h-1 bg-[#059669] rounded-full"></div>
                                Positive Sentiment
                              </div> */}
                                      {lead?.eamil[0]?.updatedAt && (
                                        <div className="text-[#6B7280] text-[9px]">
                                          {/* Sent: 3 days ago */}
                                          Sent:{" "}
                                          {moment(
                                            lead?.eamil[0]?.updatedAt
                                          ).fromNow() || ""}
                                        </div>
                                      )}
                                    </div>
                                    {/* Action Buttons: Show Thinking & Email Opened */}
                                    <div className="flex items-center gap-3">
                                      {lead?.eamil[0]?.thinking_process && <button onClick={() => {
                                        setShowThinkingModal(true);
                                        setThinkingModalData(lead?.eamil[0]?.thinking_process);
                                      }} className="inline-flex items-center gap-1 bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                        {/* Mind svg icon */}
                                        <Brain className="w-4 h-4" />
                                        Show Thinking
                                      </button>}

                                      {/* Email Opened Icon */}
                                      {lead?.eamil[0]?.email_open == 1 && (
                                        <Tooltip content="Email opened">
                                          <MailOpen className="w-4 h-4 text-[#10B981]" />
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                      Email yet to send
                                    </div>
                                  </div>
                                ))}
                              {collapsedColumns.has("email1") && (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Email 1
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("replay1")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {!collapsedColumns.has("replay1") && (
                                <div className="space-y-2">
                                  {lead?.eamil[0]?.reply &&
                                  lead?.eamil[0]?.reply?.length > 0 ? (
                                    lead?.eamil[0]?.reply?.map(
                                      (reply, index) => (
                                        <>
                                          {index > 0 && (
                                            <div className="mt-3 pt-2 border-t border-[#E5E7EB]"></div>
                                          )}
                                          {/* <div className="inline-block bg-[#ECFDF5] text-[#059669] text-[11px] px-2 py-1 rounded-[6px]">
                                    Replied
                                  </div> */}
                                          {getRepliedBadgeAccordingToSentiment(
                                            reply?.isPositive
                                          )}
                                          <div className="text-[#6B7280] text-[10px] mb-2 break-words">
                                            <p className="font-bold ">
                                              Subject : {reply?.subject || ""}
                                            </p>
                                            {reply?.content}
                                          </div>
                                          <div className="space-y-1 text-[9px] text-[#6B7280]">
                                            {/* <div>• Response time: N/A</div> */}
                                            {/* <div className="inline-flex items-center gap-1 bg-[#ECFDF5] text-[#059669] text-[9px] px-2 py-1 rounded-[4px]">
                                      <div className="w-1 h-1 bg-[#059669] rounded-full"></div>
                                      Positive Sentiment
                                    </div> */}
                                            {getSentimentBadge(
                                              reply?.isPositive
                                            )}
                                          </div>
                                          {/* Handoff Trigger Point */}
                                          {/* <div className="mt-2 p-2 bg-gradient-to-r from-[#10B981]/10 to-[#34D399]/10 border border-[#10B981]/30 rounded-[6px]">
                                    <div className="flex items-center gap-1 mb-1">
                                      <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                                      <div className="text-[#065F46] text-[8px] font-[600]">
                                        HANDOFF TRIGGERED
                                      </div>
                                    </div>
                                    <div className="text-[#065F46] text-[7px]">
                                      AI detected: Demo interest + positive
                                      sentiment
                                    </div>
                                  </div> */}
                                        </>
                                      )
                                    )
                                  ) : (
                                    <div className="space-y-2">
                                      <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                        Reply yet to receive
                                      </div>
                                      {/* <div className="space-y-1 text-[9px] text-[#6B7280]">
                                  <div>• Opened 3 days ago</div>
                                  <div>• Viewed for 45 seconds</div>
                                  <div>• Response time: N/A</div>
                                </div> */}
                                    </div>
                                  )}
                                </div>
                              )}
                              {collapsedColumns.has("replay1") && (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Reply
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("email2")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {!collapsedColumns.has("email2") &&
                                (lead?.eamil[1] ? (
                                  <div className="space-y-3">
                                    <div className="text-[#6B7280] text-[11px] leading-relaxed">
                                      {lead?.eamil[1]?.subject && (
                                        <p className="font-bold">
                                          Subject :{" "}
                                          {lead?.eamil[1]?.subject || ""}
                                        </p>
                                      )}
                                      {lead?.eamil[1]?.content || ""}
                                    </div>

                                    {/* Email Analytics */}
                                    <div className="flex flex-wrap gap-2">
                                      {/* <div className="inline-flex items-center gap-1 bg-[#ECFDF5] text-[#059669] text-[9px] px-2 py-1 rounded-[4px]">
                                <div className="w-1 h-1 bg-[#059669] rounded-full"></div>
                                Positive Sentiment
                              </div> */}
                                      {lead?.eamil[1]?.updatedAt && (
                                        <div className="text-[#6B7280] text-[9px]">
                                          {/* Sent: 3 days ago */}
                                          Sent:{" "}
                                          {moment(
                                            lead?.eamil[1]?.updatedAt
                                          ).fromNow() || ""}
                                        </div>
                                      )}
                                    </div>
                                    {/* Action Buttons: Show Thinking & Email Opened */}
                                    <div className="flex items-center gap-3">
                                      {lead?.eamil[1]?.thinking_process && <button onClick={() => {
                                        setShowThinkingModal(true);
                                        setThinkingModalData(lead?.eamil[1]?.thinking_process);
                                      }} className="inline-flex items-center gap-1 bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                        {/* Mind svg icon */}
                                        <Brain className="w-4 h-4" />
                                        Show Thinking
                                      </button>}

                                      {/* Email Opened Icon */}
                                      {lead?.eamil[1]?.email_open == 1 && (
                                        <Tooltip content="Email opened">
                                          <MailOpen className="w-4 h-4 text-[#10B981]" />
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                      Email yet to send
                                    </div>
                                  </div>
                                ))}
                              {collapsedColumns.has("email2") && (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Email 2
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("replay2")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {!collapsedColumns.has("replay2") && (
                                <div className="space-y-2">
                                  {lead?.eamil[1]?.reply &&
                                  lead?.eamil[1]?.reply?.length > 0 ? (
                                    lead?.eamil[1]?.reply?.map(
                                      (reply, index) => (
                                        <>
                                          {index > 0 && (
                                            <div className="mt-3 pt-2 border-t border-[#E5E7EB]"></div>
                                          )}
                                          {/* <div className="inline-block bg-[#ECFDF5] text-[#059669] text-[11px] px-2 py-1 rounded-[6px]">
                                    Replied
                                  </div> */}
                                          {getRepliedBadgeAccordingToSentiment(
                                            reply?.isPositive
                                          )}
                                          <div className="text-[#6B7280] text-[10px] mb-2 break-words">
                                            <p className="font-bold ">
                                              Subject : {reply?.subject || ""}
                                            </p>
                                            {reply?.content}
                                          </div>
                                          <div className="space-y-1 text-[9px] text-[#6B7280]">
                                            {/* <div>• Response time: N/A</div> */}
                                            {/* <div className="inline-flex items-center gap-1 bg-[#DBEAFE] text-[#1E40AF] text-[9px] px-2 py-1 rounded-[4px]">
                                      <div className="w-1 h-1 bg-[#1E40AF] rounded-full"></div>
                                      Neutral Sentiment
                                    </div> */}
                                            {getSentimentBadge(
                                              reply?.isPositive
                                            )}
                                          </div>
                                          {/* Handoff Trigger Point */}
                                          {/* <div className="mt-2 p-2 bg-gradient-to-r from-[#10B981]/10 to-[#34D399]/10 border border-[#10B981]/30 rounded-[6px]">
                                    <div className="flex items-center gap-1 mb-1">
                                      <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                                      <div className="text-[#065F46] text-[8px] font-[600]">
                                        HANDOFF TRIGGERED
                                      </div>
                                    </div>
                                    <div className="text-[#065F46] text-[7px]">
                                      AI detected: Demo interest + positive
                                      sentiment
                                    </div>
                                  </div> */}
                                        </>
                                      )
                                    )
                                  ) : (
                                    <div className="space-y-2">
                                      <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                        Reply yet to receive
                                      </div>
                                      {/* <div className="space-y-1 text-[9px] text-[#6B7280]">
                                  <div>• Opened 3 days ago</div>
                                  <div>• Viewed for 45 seconds</div>
                                  <div>• Response time: N/A</div>
                                </div> */}
                                    </div>
                                  )}
                                </div>
                              )}
                              {collapsedColumns.has("replay2") && (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Reply
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              className={`px-4 py-4 align-top border-r border-[#E5E7EB] transition-all duration-300 ${
                                collapsedColumns.has("email3")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {!collapsedColumns.has("email3") &&
                                (lead?.eamil[2] ? (
                                  <div className="space-y-3">
                                    <div className="text-[#6B7280] text-[11px] leading-relaxed">
                                      {lead?.eamil[2]?.subject && (
                                        <p className="font-bold">
                                          Subject :{" "}
                                          {lead?.eamil[2]?.subject || ""}
                                        </p>
                                      )}
                                      {lead?.eamil[2]?.content || ""}
                                    </div>

                                    {/* Email Analytics */}
                                    <div className="flex flex-wrap gap-2">
                                      {/* <div className="inline-flex items-center gap-1 bg-[#ECFDF5] text-[#059669] text-[9px] px-2 py-1 rounded-[4px]">
                                <div className="w-1 h-1 bg-[#059669] rounded-full"></div>
                                Positive Sentiment
                              </div> */}
                                      {lead?.eamil[2]?.updatedAt && (
                                        <div className="text-[#6B7280] text-[9px]">
                                          {/* Sent: 3 days ago */}
                                          Sent:{" "}
                                          {moment(
                                            lead?.eamil[2]?.updatedAt
                                          ).fromNow() || ""}
                                        </div>
                                      )}
                                    </div>
                                    {/* Action Buttons: Show Thinking & Email Opened */}
                                    <div className="flex items-center gap-3">
                                      {lead?.eamil[2]?.thinking_process && <button onClick={() => {
                                        setShowThinkingModal(true);
                                        setThinkingModalData(lead?.eamil[2]?.thinking_process);
                                      }} className="inline-flex items-center gap-1 bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                        {/* Mind svg icon */}
                                        <Brain className="w-4 h-4" />
                                        Show Thinking
                                      </button>}

                                      {/* Email Opened Icon */}
                                      {lead?.eamil[2]?.email_open == 1 && (
                                        <Tooltip content="Email opened">
                                          <MailOpen className="w-4 h-4 text-[#10B981]" />
                                        </Tooltip>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                      Email yet to send
                                    </div>
                                  </div>
                                ))}
                              {collapsedColumns.has("email3") && (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Email 3
                                  </div>
                                </div>
                              )}
                            </td>
                            <td
                              className={`px-4 py-4 align-top transition-all duration-300 ${
                                collapsedColumns.has("replay3")
                                  ? "max-w-[60px] min-h-[200px]"
                                  : "max-w-[200px] "
                              }`}
                            >
                              {!collapsedColumns.has("replay3") && (
                                <div className="space-y-2">
                                  {lead?.eamil[2]?.reply &&
                                  lead?.eamil[2]?.reply?.length > 0 ? (
                                    lead?.eamil[2]?.reply?.map(
                                      (reply, index) => (
                                        <>
                                          {index > 0 && (
                                            <div className="mt-3 pt-2 border-t border-[#E5E7EB]"></div>
                                          )}

                                          {/* <div className="inline-block bg-[#ECFDF5] text-[#059669] text-[11px] px-2 py-1 rounded-[6px]">
                                    Replied
                                  </div> */}
                                          {getRepliedBadgeAccordingToSentiment(
                                            reply?.isPositive
                                          )}
                                          <div className="text-[#6B7280] text-[10px] mb-2 break-words">
                                            <p className="font-bold ">
                                              Subject : {reply?.subject || ""}
                                            </p>
                                            {reply?.content}
                                          </div>
                                          <div className="space-y-1 text-[9px] text-[#6B7280]">
                                            {/* <div className="inline-block bg-[#ECFDF5] text-[#059669] text-[11px] px-2 py-1 rounded-[6px]">
                                      Meeting Confirmed
                                    </div>
                                    <div className="text-[#6B7280] text-[10px] mb-2">
                                      Added to calendar
                                    </div> */}
                                            {/* <div>• Response time: 2 hours</div> */}
                                            {/* <div className="inline-flex items-center gap-1 bg-[#ECFDF5] text-[#059669] text-[9px] px-2 py-1 rounded-[4px]">
                                      <div className="w-1 h-1 bg-[#059669] rounded-full"></div>
                                      Enthusiastic
                                    </div> */}
                                            {getSentimentBadge(
                                              reply?.isPositive
                                            )}
                                          </div>
                                          {/* Handoff Trigger Point */}
                                          {/* <div className="mt-2 p-2 bg-gradient-to-r from-[#10B981]/10 to-[#34D399]/10 border border-[#10B981]/30 rounded-[6px]">
                                    <div className="flex items-center gap-1 mb-1">
                                      <div className="w-1.5 h-1.5 bg-[#10B981] rounded-full"></div>
                                      <div className="text-[#065F46] text-[8px] font-[600]">
                                        HANDOFF TRIGGERED
                                      </div>
                                    </div>
                                    <div className="text-[#065F46] text-[7px]">
                                      AI detected: Demo interest + positive
                                      sentiment
                                    </div>
                                  </div> */}
                                        </>
                                      )
                                    )
                                  ) : (
                                    <div className="space-y-2">
                                      <div className="inline-block bg-[#DBEAFE] text-[#1E40AF] text-[11px] px-2 py-1 rounded-[6px]">
                                        Reply yet to receive
                                      </div>
                                      {/* <div className="space-y-1 text-[9px] text-[#6B7280]">
                                  <div>• Opened 3 days ago</div>
                                  <div>• Viewed for 45 seconds</div>
                                  <div>• Response time: N/A</div>
                                </div> */}
                                    </div>
                                  )}
                                </div>
                              )}
                              {collapsedColumns.has("replay3") && (
                                <div className="h-full flex items-center justify-center">
                                  <div className="text-[#6B7280] text-[10px] transform rotate-90 whitespace-nowrap">
                                    Reply
                                  </div>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Timeline Status Bar */}
            <div className="mt-4 flex justify-between items-center text-[12px] text-[#6B7280]">
              <div className="flex items-center gap-4">
                <span>
                  Timeline Status:{" "}
                  {collapsedColumns.size > 0
                    ? `${collapsedColumns.size} columns collapsed`
                    : "All columns expanded"}
                </span>
                <span>Zoom: {Math.round(timelineZoom * 100)}%</span>
              </div>
              <div className="flex items-center gap-2">
                {collapsedColumns.size > 0 && (
                  <button
                    onClick={() => setCollapsedColumns(new Set())}
                    className="text-[#5E78F4] hover:text-[#4A63D2] transition-colors"
                  >
                    Expand All
                  </button>
                )}
                <span className="text-[#E5E7EB]">•</span>
                <span>
                  {leadsEmails?.filter((lead) => {
                    if (statusFilter === "active") {
                      return lead.emailsSent < 3;
                    } else if (statusFilter === "completed") {
                      return lead.emailsSent === 3;
                    }
                    return true;
                  }).length || 0}{" "}
                  leads •{" "}
                  {leadsEmails
                    ?.filter((lead) => {
                      if (statusFilter === "active") {
                        return lead.emailsSent < 3;
                      } else if (statusFilter === "completed") {
                        return lead.emailsSent === 3;
                      }
                      return true;
                    })
                    .reduce((acc, email) => acc + email?.eamil?.length, 0) ||
                    0}{" "}
                  total email interactions
                </span>
              </div>
            </div>
          </div>
        ) : (
          <div>
            {leads.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {leads.map((lead) => (
                  <LeadCard key={lead.id} lead={lead} />
                ))}
              </div>
            ) : (
              <div className="bg-[#FAFBFC] rounded-[20px] border border-[#E5E7EB] p-8 text-center">
                <User className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
                <h3 className="font-[600] text-[#374151] text-[18px] mb-2">
                  No leads yet
                </h3>
                <p className="text-[#6B7280] text-[14px] mb-4">
                  Import or add leads to start your email outreach
                </p>
                <button className="py-2 px-4 rounded-[15px] font-[500] text-[14px] bg-[#5E78F4] text-white hover:bg-[#4A63D2] transition-colors">
                  Add Leads
                </button>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Details Drawer */}
      <DetailsDrawer
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        drawerData={drawerData}
        setDrawerData={setDrawerData}
        drawerLoading={drawerLoading}
        activeTab={null}
        setIsUpgradeModalOpen={setIsUpgradeModalOpen}
      />

      {/* Upgrade Modal */}
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        setIsOpen={setIsUpgradeModalOpen}
      />

      {/* Failure Modal */}
      <FailureModal
        open={openFailureModal}
        removeModal={removeModal}
        successFailureMessage={successFailureMessage}
      />
      {/* thinking modal */}
      <Modal
        show={showThinkingModal}
        onClose={() => {
          setShowThinkingModal(false);
          setThinkingModalData(null);
        }}
        dismissible
      >
        <Modal.Header className="border-b-2">
          <h3 className="text-lg flex items-center gap-2 font-medium text-gray-900 dark:text-white">
            <Brain/>
            Thinking Process
          </h3>
        </Modal.Header>
        <Modal.Body className="overflow-y-auto">
          <div className="">
            <p className="my-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              {thinkingModalData}
              {/* Lorem ipsum dolor sit amet consectetur adipisicing elit. Reprehenderit a deserunt rem voluptates tenetur blanditiis nisi, nostrum aspernatur possimus quos iure eum rerum provident omnis ut minus placeat, quam aliquid accusamus? Dolorem suscipit minima earum ratione tenetur corporis, quo tempora! Illum voluptate asperiores quod delectus eligendi alias consectetur corporis? Cumque explicabo quisquam laboriosam asperiores, ratione aut, officiis saepe hic fuga dolorum modi quas autem facilis doloremque itaque, veniam debitis dignissimos nisi necessitatibus. Minus vitae provident natus officiis dolor aliquam nostrum enim, assumenda corporis corrupti ea alias velit quod, quia repellat dicta! Ad esse natus dignissimos, veniam nemo ratione vitae quibusdam. */}
            </p>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};
